import React, { useState, useEffect } from 'react';
import FilterTypeSelector from './FilterTypeSelector';
import FilterInput from './FilterInput';
import FilterButton from './FilterButton';
import ActiveFilterDisplay from './ActiveFilterDisplay';
import { getFilterTypeConfig } from '../utils/filterValidation';
import './FilterBar.css';

const FilterBar = ({ activeFilter, onFilterApply, onFilterClear, loading, disabled }) => {
  const [selectedFilterType, setSelectedFilterType] = useState('airport');
  const [inputValue, setInputValue] = useState('');

  // Sync internal state with activeFilter prop (for URL parameter restoration)
  useEffect(() => {
    if (activeFilter) {
      setSelectedFilterType(activeFilter.type);
      // Convert to uppercase for registration and airport filters
      const displayValue = (activeFilter.type === 'registration' || activeFilter.type === 'airport')
        ? activeFilter.value.toUpperCase() 
        : activeFilter.value;
      setInputValue(displayValue);
    } else {
      // Reset to default when no active filter
      setSelectedFilterType('airport');
      setInputValue('');
    }
  }, [activeFilter]);

  // Get current filter configuration
  const currentFilterConfig = getFilterTypeConfig(selectedFilterType);

  const handleFilterTypeChange = (filterType) => {
    setSelectedFilterType(filterType);
    setInputValue(''); // Clear input when filter type changes
  };

  const handleInputChange = (e) => {
    let newValue = e.target.value;
    
    // Convert to uppercase for registration and airport filters
    if (selectedFilterType === 'registration' || selectedFilterType === 'airport') {
      newValue = newValue.toUpperCase();
    }
    
    setInputValue(newValue);
  };

  const handleApplyFilter = () => {
    const trimmedValue = inputValue.trim();
    
    if (trimmedValue && onFilterApply) {
      const filter = {
        type: selectedFilterType,
        value: trimmedValue,
        displayLabel: `${currentFilterConfig?.label}: ${trimmedValue}`
      };
      onFilterApply(filter);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleApplyFilter();
    }
  };

  const handleClearFilter = () => {
    if (onFilterClear) {
      onFilterClear();
    }
  };

  const isApplyDisabled = loading || disabled || !inputValue.trim();

  return (
    <div className="filter-bar">
      <div className="filter-bar-content">
        {activeFilter ? (
          /* Show only Active Filter Display when filter is active */
          <ActiveFilterDisplay
            activeFilter={activeFilter}
            onClear={handleClearFilter}
            disabled={disabled}
          />
        ) : (
          /* Show Filter Controls in horizontal layout when no filter is active */
          <div className="filter-controls">
            <FilterTypeSelector
              selectedFilterType={selectedFilterType}
              onFilterTypeChange={handleFilterTypeChange}
              disabled={disabled}
            />
            <div className="filter-input-group">
              <FilterInput
                value={inputValue}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                placeholder={currentFilterConfig?.placeholder || 'Enter filter value'}
                disabled={disabled}
                ariaLabel={`Filter by ${currentFilterConfig?.label.toLowerCase()}`}
                description={currentFilterConfig?.description}
              />
              <FilterButton
                onClick={handleApplyFilter}
                disabled={isApplyDisabled}
                loading={loading}
                ariaLabel="Apply filter"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FilterBar;